<template>
  <div class="callback-settings-page">
    <el-card shadow="never">
      <div slot="header" class="callback-header"><strong>默认通知地址</strong><el-tag size="small" :type="loaded && savedUrl ? 'success' : 'info'">{{ loaded ? (savedUrl ? '已配置' : '未启用') : '未读取' }}</el-tag></div>
      <p class="callback-intro">配置本主账号的默认通知地址，用于接收时效、偏航、停车、离线、途中围栏、超速、疲劳和起终点通知。子账号共用此地址。</p>
      <el-alert v-if="!canEdit" class="callback-alert" title="当前账号仅可查看，需由有编辑权限的主账号修改。" type="info" :closable="false" show-icon />
      <el-alert v-if="errorMessage" class="callback-alert" :title="errorMessage" type="error" :closable="false" show-icon />
      <el-form ref="callbackForm" v-loading="loading" :model="form" :rules="rules" label-position="top" class="callback-form" @submit.native.prevent="save">
        <el-form-item label="通知地址" prop="defaultNotifyUrl">
          <el-input v-model.trim="form.defaultNotifyUrl" :disabled="!canEdit || loading || saving || !loaded" maxlength="1000" clearable placeholder="https://your-service.example.com/in-transit/notify" autocomplete="off" />
          <p class="callback-hint">请输入可公网访问的 HTTPS 地址。留空并保存后停用默认通知；保存配置不会发送测试通知。</p>
        </el-form-item>
        <el-form-item>
          <el-button v-if="canEdit" type="primary" :loading="saving" :disabled="loading || !loaded" @click="save">保存配置</el-button>
          <el-button icon="el-icon-refresh" :loading="loading" :disabled="saving" @click="fetchSettings">重新加载</el-button>
        </el-form-item>
      </el-form>
      <div class="callback-protocol">
        <h3>接收方式</h3>
        <p>系统以 POST、application/x-www-form-urlencoded 发送通知，接收端通过 type 区分事件。处理成功须返回文本 success 或 JSON 字符串 "success"；仅返回 HTTP 2xx 不代表确认成功。</p>
        <p>通知可能重复，接收端应先持久化再应答，并按业务事件去重。告警通知依赖运输监控及对应规则生效，配置地址不会自动开启监控或补发历史事件。</p>
        <router-link v-if="canViewDocs" to="/api-docs?api=callback-timeliness">查看通知参数及示例 <i class="el-icon-arrow-right" /></router-link>
      </div>
    </el-card>
  </div>
</template>

<script>
import request from '@/utils/request'
import { authState, hasPermission } from '@/utils/auth'

export default {
  name: 'CallbackSettings',
  data() {
    return {
      form: { defaultNotifyUrl: '' }, savedUrl: '', loading: false, saving: false, loaded: false, errorMessage: '', requestId: 0,
      rules: { defaultNotifyUrl: [{ validator: this.validateNotifyUrl, trigger: 'blur' }] }
    }
  },
  computed: {
    canEdit() { return Boolean(authState.user && authState.user.accountType === 'MAIN' && hasPermission('system:callback:edit')) },
    canViewDocs() { return hasPermission('system:api-doc:list') }
  },
  created() { this.fetchSettings() },
  beforeDestroy() { this.requestId++ },
  methods: {
    validateNotifyUrl(rule, value, callback) {
      if (!value) return callback()
      try {
        const url = new URL(value)
        if (url.protocol !== 'https:' || !url.hostname || url.username || url.password || url.hash) throw new Error('invalid')
        callback()
      } catch (error) { callback(new Error('请输入不含用户名、密码和片段的 HTTPS 地址')) }
    },
    async fetchSettings() {
      if (this.saving) return
      const requestId = ++this.requestId
      this.loading = true
      this.loaded = false
      this.errorMessage = ''
      try {
        const response = await request.get('/webserver/openapi/callback-settings')
        if (requestId !== this.requestId) return
        this.savedUrl = response.data.defaultNotifyUrl || ''
        this.form.defaultNotifyUrl = this.savedUrl
        this.loaded = true
        this.$nextTick(() => this.$refs.callbackForm && this.$refs.callbackForm.clearValidate())
      } catch (error) { if (requestId === this.requestId) this.errorMessage = error.msg || '读取回调配置失败，请重新加载' }
      finally { if (requestId === this.requestId) this.loading = false }
    },
    save() {
      if (!this.canEdit || this.saving || this.loading || !this.loaded) return
      this.$refs.callbackForm.validate(async valid => {
        if (!valid || this.saving) return
        this.saving = true
        this.errorMessage = ''
        const requestId = this.requestId
        try {
          const response = await request.put('/webserver/openapi/callback-settings', { defaultNotifyUrl: this.form.defaultNotifyUrl.trim() })
          if (requestId !== this.requestId) return
          this.savedUrl = response.data.defaultNotifyUrl || ''
          this.form.defaultNotifyUrl = this.savedUrl
          this.$message.success(this.savedUrl ? '默认通知地址已保存' : '默认通知已停用')
        } catch (error) { if (requestId === this.requestId) this.errorMessage = error.msg || '保存失败，请重试' }
        finally { if (requestId === this.requestId) this.saving = false }
      })
    }
  }
}
</script>

<style scoped>
.callback-settings-page { max-width: 1080px; }
.callback-header { display: flex; gap: 14px; align-items: center; }
.callback-header strong { font-size: 17px; }
.callback-intro { margin: 0 0 22px; color: #606266; font-size: 14px; line-height: 1.8; }
.callback-alert { margin-bottom: 18px; }
.callback-form { max-width: 780px; }
.callback-hint { margin: 10px 0 0; color: #909399; font-size: 13px; line-height: 1.7; }
.callback-protocol { padding: 20px 24px; border-radius: 6px; background: #f5f7fb; color: #606266; font-size: 14px; line-height: 1.8; }
.callback-protocol h3 { margin: 0 0 10px; color: #303133; font-size: 15px; }
.callback-protocol p { margin: 8px 0; }
.callback-protocol a { display: inline-block; margin-top: 8px; color: #1677ff; text-decoration: none; }
</style>
